import * as React from 'react'

import { XIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  GroupConnectorLines,
  GroupIndent,
} from '~/client/src/shared/components/GroupHeader'
import LocationIcon from '~/client/src/shared/components/LocationIcon/LocationIcon'
import { LocationReference } from '~/client/src/shared/components/ObjectReference/LocationReference'
import PermitListItem from '~/client/src/shared/components/PermitListItem'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import Workflow from '~/client/src/shared/models/Workflow'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'

import { StackMapLink } from './StackMapLink'
import { IStackLocationRow } from './stackRows'
import { ITaggedMap } from './taggedMaps'

export interface IStackListProps {
  rows: IStackLocationRow[]
  getLocation: (itemId: string) => LocationBase | undefined
  workflows: Workflow[]
  onSelectLocation: (itemId: string) => void
  onSelectWorkflow: (workflow: Workflow) => void
  taggedMapsOf: (location: LocationBase) => ITaggedMap[]
  isMapSwitchLocked: boolean
  onClose: () => void
}

interface IProps extends IStackListProps {
  locationAttributesStore?: LocationAttributesStore
  // Focus lands on the first row as the list opens, so its keys work at once (the map popup).
  focusesFirstRow?: boolean
}

export interface IStackHeading {
  location?: LocationBase
  title: string
  subtitle?: string
}

export function getStackHeading(
  rows: IStackLocationRow[],
  workflowCount: number,
  getLocation: (itemId: string) => LocationBase | undefined,
): IStackHeading {
  const roots = rows.filter(row => row.depth === 0)
  const location = roots.length === 1 ? getLocation(roots[0].itemId) : null
  return location
    ? {
        location,
        title: location.name,
        subtitle: Localization.translator.xLocations(rows.length),
      }
    : {
        title: Localization.translator.xObjectsHere(
          rows.length + workflowCount,
        ),
      }
}

interface IStackListHeaderProps {
  rows: IStackLocationRow[]
  workflowCount: number
  getLocation: (itemId: string) => LocationBase | undefined
  onClose: () => void
  titleAs?: React.ElementType
}

export const StackListHeader: React.FC<IStackListHeaderProps> = observer(
  ({ rows, workflowCount, getLocation, onClose, titleAs: Title = 'h2' }) => {
    const { location, title, subtitle } = getStackHeading(
      rows,
      workflowCount,
      getLocation,
    )
    return (
      <header className="tw:flex tw:items-start tw:gap-2 tw:border-b tw:border-border tw:py-2 tw:pr-2 tw:pl-3">
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
          <Title className="tw:m-0 tw:flex tw:min-w-0 tw:items-center tw:gap-2 tw:text-title-sm tw:font-semibold tw:text-foreground">
            {location && (
              <LocationIcon
                iconName={location.iconName}
                color={location.color}
                size={20}
                className="tw:size-5 tw:shrink-0 tw:text-muted-foreground"
              />
            )}
            <span className="tw:truncate">{title}</span>
          </Title>
          {subtitle && (
            <span className="tw:text-app-micro tw:text-muted-foreground">
              {subtitle}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon-xs"
          className="tw:shrink-0"
          data-stack-close=""
          onClick={onClose}
          aria-label={Localization.translator.close}
        >
          <XIcon aria-hidden="true" className="tw:size-4" />
        </Button>
      </header>
    )
  },
)

const LIST_CLASS = 'tw:m-0 tw:list-none tw:p-0'
const FOCUS_RING_CLASS =
  'tw:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-inset tw:focus-visible:ring-ring'

@inject('locationAttributesStore')
@observer
export default class StackList extends React.Component<IProps> {
  private readonly containerRef = React.createRef<HTMLDivElement>()

  // A frame later: a map popup's content is portalled into the page only once mapbox adds it.
  public componentDidMount(): void {
    if (!this.props.focusesFirstRow) return
    requestAnimationFrame(() =>
      this.containerRef.current
        ?.querySelector<HTMLElement>('[data-stack-row]')
        ?.focus({ preventScroll: true }),
    )
  }

  public render() {
    const { rows, workflows } = this.props

    return (
      <div
        ref={this.containerRef}
        className="tw:flex tw:flex-col tw:py-1"
        onKeyDown={this.onKeyDown}
      >
        {!!rows.length && (
          <ul role="list" aria-label={this.listLabel} className={LIST_CLASS}>
            {rows.map(this.renderLocationRow)}
          </ul>
        )}
        {!!workflows.length && this.renderWorkflows()}
      </div>
    )
  }

  private renderLocationRow = ({ itemId, depth }: IStackLocationRow) => {
    const location = this.props.getLocation(itemId)
    if (!location) return null
    const maps = this.props.taggedMapsOf(location)

    return (
      <li
        key={itemId}
        className="tw:relative tw:flex tw:items-center tw:rounded-md tw:hover:bg-accent-soft"
      >
        <GroupConnectorLines depth={depth} />
        <button
          type="button"
          data-stack-row=""
          onClick={() => this.props.onSelectLocation(itemId)}
          className={`tw:flex tw:min-h-9 tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:items-center tw:rounded-md tw:border-0 tw:bg-transparent tw:px-2 tw:py-1.5 tw:text-left ${FOCUS_RING_CLASS}`}
        >
          <GroupIndent depth={depth} />
          <LocationReference
            iconName={location.iconName}
            color={location.color}
            variant="plain"
            size="dense"
            parentNames={depth === 0 ? this.parentNamesOf(location) : []}
            resourceCode={location.stxResourceId}
          >
            {location.name}
          </LocationReference>
        </button>
        {maps.length > 0 && (
          <StackMapLink maps={maps} isDisabled={this.props.isMapSwitchLocked} />
        )}
      </li>
    )
  }

  private renderWorkflows() {
    const { workflows, onSelectWorkflow } = this.props
    const label = Localization.translator.workflowsHereX(workflows.length)

    return (
      <>
        <p className="tw:m-0 tw:px-2 tw:pt-3 tw:pb-1 tw:text-label-md tw:text-muted-foreground">
          {label}
        </p>
        <ul
          role="list"
          aria-label={label}
          className={`${LIST_CLASS} tw:divide-y tw:divide-border`}
        >
          {workflows.map(workflow => (
            <li
              key={workflow.id}
              tabIndex={0}
              data-stack-row=""
              onKeyDown={event => {
                if (event.target !== event.currentTarget) return
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                onSelectWorkflow(workflow)
              }}
              className={`tw:px-2 ${FOCUS_RING_CLASS}`}
            >
              <PermitListItem permit={workflow} onClick={onSelectWorkflow} />
            </li>
          ))}
        </ul>
      </>
    )
  }

  private onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.props.onClose()
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

    const rows = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>('[data-stack-row]'),
    )
    const active = document.activeElement
    const index = rows.findIndex(row => row.closest('li')?.contains(active))
    const next =
      event.key === 'ArrowDown'
        ? rows[Math.min(index + 1, rows.length - 1)]
        : rows[Math.max(index - 1, 0)]
    if (!next) return

    event.preventDefault()
    next.focus()
  }

  private parentNamesOf(location: LocationBase): string[] {
    return this.props.locationAttributesStore
      .getAncestryChain(location)
      .slice(0, -1)
      .map(ancestor => ancestor.name)
  }

  private get listLabel(): string {
    const { rows, getLocation } = this.props
    const names = rows.map(row => getLocation(row.itemId)?.name).filter(Boolean)
    return Localization.translator.xObjectsAtThisSpot(rows.length, names)
  }
}
